<script setup lang="ts">
import { INQUIRY_ITEM } from '@/types'
import { endpoints } from '@/utils/endpoints'
import {
  getInitialInquiryStatus,
  sendChangedInquiryStatusEvent,
} from '@/utils/mixpanel'
import { format, set } from 'date-fns'

const props = defineProps({
  selectedInquiry: {
    type: Object as PropType<INQUIRY_ITEM>,
    required: false,
  },
  desktopDetails: {
    type: Boolean,
    required: false,
  },
})

const inquiriesStore = props.desktopDetails ? useDesktopDetailsStore() : useInquiriesStore()
const route = useRoute()
const appStore = useAppStore()
const isDisabled = ref(false)
const currentSpotName = appStore.spotInfo.name

const inquiry = computed(() => {
  if (props.desktopDetails) {
    return inquiriesStore.inquiry
  } else {
    return (
      inquiriesStore.getInquiryById(Number(route.query.inquiry_id)) ||
      JSON.parse(JSON.stringify(props.selectedInquiry))
    )
  }
})

const showAcceptOrderConfirmationPopUp = ref(false)
const showRefundOrderConfirmationPopUp = ref(false)
const showRejectOrderConfirmationPopUp = ref(false)

const noteForDriver = ref(
  appStore.spotInfo.order_configs.default_pickup_instructions,
)

const rejectionComment = ref('')

const deliveryScheduledInOptions = [
  { label: '15 minutes', shortLabel: '15 min' },
  { label: '30 minutes', shortLabel: '30 min' },
  { label: '1 hour', shortLabel: '1 hr' },
  { label: '1 hour and 30 minutes', shortLabel: '1 hr 30 min' },
  { label: '2 hours', shortLabel: '2 hr' },
]

const defaultExternalDeliveryPickupTime = ref(
  deliveryScheduledInOptions.find(
    (option) =>
      option.label ==
      appStore.spotInfo.order_configs.default_external_delivery_pick_up_time,
  ),
)

const handleAcceptOrder = async () => {
  isDisabled.value = true
  const oldStatus = getInitialInquiryStatus(inquiry.value!)!
   inquiry.value!.delivery_order_status = 'accepted, arrange pickup'

  const pickupTime = inquiry.value.order_future_date_or_time
    ? 'future date or time'
    : defaultExternalDeliveryPickupTime.value!.label

  sendChangedInquiryStatusEvent(inquiry.value, oldStatus, route.name as string)

   await inquiriesStore.updateDoorDashDeliveryInquiry(
    inquiry.value!,
    pickupTime,
    noteForDriver.value,
  )

  showAcceptOrderConfirmationPopUp.value = false
  isDisabled.value = false
}

const handleRefundOrder = async () => {
  isDisabled.value = true
  const oldStatus = getInitialInquiryStatus(inquiry.value!)!

  await useApi(
    `${endpoints().cancelDelivery(Number(route.query.spot_id), inquiry.value!.order[0].id!)}`,
  ).post()

  inquiry.value!.delivery_order_status = 'rejected'

  sendChangedInquiryStatusEvent(inquiry.value, oldStatus, route.name as string)
  await inquiriesStore.updateInquiry(inquiry.value!)

  showRefundOrderConfirmationPopUp.value = false
  await handleRejectionComment()
  isDisabled.value = false
}

const handleCloseRefundOrderConfirmationPopUp = () => {
  showRefundOrderConfirmationPopUp.value = false
  rejectionComment.value = ''
  inquiriesStore.setSelectedInquiryStatus(inquiry.value!.delivery_order_status)
}

const handleCloseAcceptOrderConfirmationPopUp = () => {
  showAcceptOrderConfirmationPopUp.value = false

  inquiriesStore.setSelectedInquiryStatus(inquiry.value!.delivery_order_status)
}

const handleSelectScheduledTime = (time: {
  label: string
  shortLabel: string
}) => {
  defaultExternalDeliveryPickupTime.value = time
}

const handleRejectOrder = async () => {
  const oldStatus = getInitialInquiryStatus(inquiry.value!)!

  isDisabled.value = true
  showRejectOrderConfirmationPopUp.value = false
  inquiriesStore.setSelectedInquiryStatus('rejected')

  inquiry.value!.delivery_order_status = inquiriesStore.selectedInquiryStatus!

  sendChangedInquiryStatusEvent(inquiry.value, oldStatus, route.name as string)
  await inquiriesStore.updateInquiry(inquiry.value!)

  await handleRejectionComment()

  isDisabled.value = false
}

const handleRejectionComment= async() => {
  const reason = rejectionComment.value.trim()
  if (reason.length > 0) {
    const comment = await inquiriesStore.createInquiryComment(
      inquiry!.value!.id,
      reason,
      'manual',
    )
    inquiriesStore.comments.push(comment)
    rejectionComment.value = ''
  }  
}

const handleCloseRejectOrderConfirmationPopUp = () => {
  showRejectOrderConfirmationPopUp.value = false
  rejectionComment.value = ''
  inquiriesStore.setSelectedInquiryStatus(inquiry.value!.delivery_order_status)
}

const setRejectionComment = () => {
  rejectionComment.value = `Dear ${inquiry!.value!.name},\nWe are sorry, but we cannot accept your order. \n\nBest Regards,\n${currentSpotName}`
}

const formattedDate = computed(() => {
  return format(
    set(new Date(), {
      year: inquiry.value!.year!,
      month: inquiry.value!.month! - 1,
      date: inquiry.value!.day!,
      hours: inquiry.value!.hour!,
      minutes: inquiry.value!.min!,
      seconds: 0,
    }),
    "iii, M/d/yyyy 'at' h:mm a",
  )
})
watch(
  () => inquiriesStore.openArrangePickupModal,
  (newValue) => {
    if (newValue === true) {
      showAcceptOrderConfirmationPopUp.value = true
      inquiriesStore.setOpenArrangePickupModal(false)
    }
  },
  { immediate: true }
)

watch(
  () => inquiriesStore.selectedInquiryStatus,
  (newValue) => {
    if (newValue === undefined) return

    if (newValue === 'received') return

    if (
      newValue === 'accepted, arrange pickup' &&
      inquiry.value!.delivery_order_status !== 'accepted, arrange pickup'
    ) {
      showAcceptOrderConfirmationPopUp.value = true
      return
    } else if (
      newValue === 'rejected' &&
      inquiry.value!.delivery_order_status === 'accepted, arrange pickup'
    ) {
      setRejectionComment()
      showRefundOrderConfirmationPopUp.value = true
      return
    } else if (
      newValue === 'rejected' &&
      inquiry.value!.delivery_order_status === 'received'
    ) {
      setRejectionComment()
      showRejectOrderConfirmationPopUp.value = true
      return
    }
  },
)
</script>

<template>
  <AppDialog
    :is-open="showRefundOrderConfirmationPopUp"
    :can-close="true"
    :has-close-button="false"
    :teleport-to="
      appStore.spotHopperDesktopDetailsPage === true ? '#sidebar' : undefined
    "
    @close="handleCloseRefundOrderConfirmationPopUp"
  >
    <template #content>
      <div class="px-4 pb-4 text-center text-brand-black">
        <p>
          Since this order was previously accepted, which triggered a payment,
          rejecting this order will result in issuing a refund.
        </p>
        <p class="mt-3 font-bold">
          Are you sure you want to proceed and reject this order now?
        </p>
        <textarea
          v-model="rejectionComment"
          rows="10"
          placeholder="Reason for rejection..."
          class="mt-2 max-h-[200px] w-full rounded-lg border border-[#EEF1F7] bg-[#EEF1F7] p-3 text-brand-black placeholder:italic"
        ></textarea>

        <div class="flex gap-4">
          <AppButton
            tabindex="-1"
            class="mt-4 !bg-[#EEF1F7]"
            size="md"
            variant="secondary"
            :is-rounded="true"
            :is-wide="true"
            @click="handleCloseRefundOrderConfirmationPopUp"
          >
            No
          </AppButton>
          <AppButton
            class="mt-4 !bg-[#6DDF9D] !text-brand-black !ring-0"
            variant="primary"
            size="md"
            :is-rounded="true"
            :is-wide="true"
            :disabled="isDisabled"
            @click="handleRefundOrder"
          >
            Yes
          </AppButton>
        </div>
      </div>
    </template>
  </AppDialog>
  <AppDialog
    :is-open="showAcceptOrderConfirmationPopUp"
    :can-close="true"
    :has-close-button="false"
    :teleport-to="
      appStore.spotHopperDesktopDetailsPage === true ? '#sidebar' : undefined
    "
    @close="handleCloseAcceptOrderConfirmationPopUp"
  >
    <template #title>
      <div>
        <p class="font-semibold text-brand-black">
          Please confirm the time for pickup
        </p>
      </div>
    </template>
    <template #content>
      <div class="px-4 pb-4 text-brand-black">
        <p v-if="inquiry!.order_future_date_or_time">
          I need an order delivered on {{ formattedDate }}
        </p>
        <div v-else class="mb-5 flex flex-wrap justify-start gap-2">
          <AppButton
            v-for="(time, index) in deliveryScheduledInOptions"
            :key="index"
            class="mt-4 min-w-[7rem] text-sm !ring-0"
            :variant="
              defaultExternalDeliveryPickupTime!.label == time.label
                ? 'primary'
                : 'secondary'
            "
            size="md"
            :is-rounded="true"
            :is-wide="false"
            :disabled="isDisabled"
            @click="handleSelectScheduledTime(time)"
          >
            {{ time.shortLabel }}
          </AppButton>
        </div>
        <p class="font-bold text-brand-black">
          Pickup Instructions for Driver:
        </p>
        <textarea
          v-model="noteForDriver"
          rows="10"
          placeholder="Any instructions you want delivery driver to know..."
          class="mt-5 w-full rounded-lg border border-[#EEF1F7] bg-[#EEF1F7] p-3 text-brand-black placeholder:italic"
        ></textarea>
        <div class="flex gap-4">
          <AppButton
            tabindex="-1"
            class="mt-4 !bg-[#EEF1F7]"
            size="md"
            variant="secondary"
            :is-rounded="true"
            :is-wide="true"
            @click="handleCloseAcceptOrderConfirmationPopUp"
          >
            No
          </AppButton>
          <AppButton
            class="mt-4 !bg-[#6DDF9D] !text-brand-black !ring-0"
            variant="primary"
            size="md"
            :is-rounded="true"
            :is-wide="true"
            :disabled="isDisabled"
            @click="handleAcceptOrder"
          >
            Yes
          </AppButton>
        </div>
      </div>
    </template>
  </AppDialog>
  <AppDialog
    :is-open="showRejectOrderConfirmationPopUp"
    :can-close="true"
    :has-close-button="false"
    :teleport-to="
      appStore.spotHopperDesktopDetailsPage === true ? '#sidebar' : undefined
    "
    @close="handleCloseRejectOrderConfirmationPopUp"
  >
    <template #content>
      <div class="px-4 pb-4 text-brand-black">
        <p v-if="inquiry?.payment_status" class="font-bold text-brand-black">
          Please confirm that you want to reject this order. If this order is
          rejected, you will not be able to change your mind, accept the order
          and charge this customer for this order.
        </p>
        <p v-else class="text-brand-black">
          Please provide a reason for rejection that will be emailed to the customer:
        </p>
        <p v-if="inquiry?.payment_status" class="mt-2 text-brand-black">
          Please provide a reason for rejection that will be emailed to the customer:
        </p>
        <textarea
          v-model="rejectionComment"
          rows="10"
          placeholder="Reason for rejection..."
          class="mt-5 max-h-[200px] w-full rounded-lg border border-[#EEF1F7] bg-[#EEF1F7] p-3 text-brand-black placeholder:italic"
        ></textarea>
        <div class="flex gap-4">
          <AppButton
            tabindex="-1"
            class="mt-4 !bg-[#EEF1F7]"
            size="md"
            variant="secondary"
            :is-rounded="true"
            :is-wide="true"
            @click="handleCloseRejectOrderConfirmationPopUp"
          >
            No
          </AppButton>
          <AppButton
            class="mt-4 !bg-[#6DDF9D] !text-brand-black !ring-0"
            variant="primary"
            size="md"
            :is-rounded="true"
            :is-wide="true"
            :disabled="isDisabled"
            @click="handleRejectOrder"
          >
            Yes
          </AppButton>
        </div>
      </div>
    </template>
  </AppDialog>
</template>
